<template>
  <Teleport to="body">
  <div class="modal-overlay">
    <div class="edit-technician-page" @click="closeDropdowns" @click.stop>
      <!-- 头部布局：标题 + 语言下拉 + 关闭按钮 -->
      <div class="modal-header">
        <div class="header-left">
          <h1 class="page-title">{{ t('title') }}</h1>
          <!-- 语言选择 -->
          <div class="language-select-wrapper" @click.stop>
            <div
              class="custom-select"
              :class="{ 'is-open': dropdownOpen === 'language' }"
              @click="toggleDropdown('language')"
            >
              <span>{{ currentLang === 'zh' ? '中文' : 'English' }}</span>
              <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
            </div>
            <div v-if="dropdownOpen === 'language'" class="custom-dropdown">
              <div class="dropdown-item" :class="{ selected: currentLang === 'en' }" @click="setLanguage('en')">English</div>
              <div class="dropdown-item" :class="{ selected: currentLang === 'zh' }" @click="setLanguage('zh')">中文</div>
            </div>
          </div>
        </div>
        <button class="btn-close" @click="handleClose">
          <img :src="closeFillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <div class="form-container">
          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('type') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.type }" @click.stop @focusout="handleTypeFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'type' }"
                @click="toggleDropdown('type')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.type }">
                  {{ formData.type ? getLabel(formData.type, typeOptions) : t('ph_type') }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.type" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'type'" class="custom-dropdown">
                <div
                  v-for="opt in typeOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.type === opt.value }"
                  @click="selectOption('type', opt.value)"
                >
                  {{ getLabel(opt.value, typeOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('itemName') }}</label>
            <div class="input-wrapper" :class="{ error: errors.itemName || errors.itemNameLang }">
              <input
                :value="currentLang === 'zh' ? formData.itemNameZh : formData.itemName"
                @input="handleItemNameInput"
                type="text"
                class="form-input"
                :placeholder="t('ph_name')"
                maxlength="200"
                @blur="handleItemNameBlur"
              />
              <img v-if="errors.itemName || errors.itemNameLang" :src="alertIcon" alt="error" class="error-icon" />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('itemIcon') }}</label>
            <div class="upload-container" :class="{ error: errors.icon }">
              <div
                v-if="!previewUrl"
                class="upload-area"
                @click="triggerFileUpload"
                @click.stop
              >
                <img :src="photoIcon" alt="upload" class="upload-icon" />
                <span class="upload-text">{{ t('upload') }}</span>
              </div>
              <div v-else class="uploaded-image-wrapper">
                <img :src="previewUrl" alt="preview" class="uploaded-image" />
                <!-- 添加上传覆盖层，允许用户点击更换图片 -->
                <div class="upload-overlay" @click="triggerFileUpload" @click.stop>
                  <img :src="photoIcon" alt="upload" class="upload-icon-small" />
                  <span class="upload-text-small">{{ t('upload') }}</span>
                </div>
                <div class="delete-overlay" @click="handleDeleteImage" @click.stop>
                  <img :src="deleteIcon" alt="delete" class="delete-icon" />
                  <span class="delete-text">{{ t('delete') }}</span>
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="file-input"
                @change="handleFileUpload"
                id="edit-item-file-input"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('duration') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.duration }" @click.stop @focusout="handleDurationFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'duration' }"
                @click="toggleDropdown('duration')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.duration }">
                  {{ formData.duration ? getDurationLabel(formData.duration) : t('ph_duration') }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.duration" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'duration'" class="custom-dropdown">
                <div
                  v-for="option in durationOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.duration === option.value }"
                  @click="selectOption('duration', option.value)"
                >
                  {{ option.label }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('seasons') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.seasons }" @click.stop @focusout="handleSeasonsFocusOut">
              <div
                class="custom-select custom-select--seasons"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'seasons' }"
                @click="toggleDropdown('seasons')"
              >
                <span
                  v-if="formData.seasons.length === 0"
                  class="custom-select-value placeholder-text"
                >
                  {{ t('ph_seasons') }}
                </span>
                <div v-else class="season-select-tags">
                  <span
                    v-for="option in selectedSeasonOptions"
                    :key="option.value"
                    class="season-tag"
                  >
                    {{ option.label }}
                  </span>
                </div>
                <div class="select-trail">
                  <img v-if="errors.seasons" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'seasons'" class="custom-dropdown checkbox-dropdown">
                <div class="dropdown-item" :class="{ selected: isAllSeasonsSelected }" @click.stop="toggleAllSeasons">
                  <div class="checkbox-wrapper">
                    <div class="checkbox" :class="{ checked: isAllSeasonsSelected }">
                      <svg v-if="isAllSeasonsSelected" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label all-label">{{ t('all') }}</span>
                  </div>
                </div>
                <div
                  v-for="option in seasonOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.seasons.includes(option.value) }"
                  @click.stop="toggleSeason(option.value)"
                >
                  <div class="checkbox-wrapper">
                    <div
                      class="checkbox"
                      :class="{ checked: formData.seasons.includes(option.value) }"
                    >
                      <svg
                        v-if="formData.seasons.includes(option.value)"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('section') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.section }" @click.stop @focusout="handleSectionFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'section' }"
                @click="toggleDropdown('section')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.section }">
                  {{
                    formData.section ? getLabel(formData.section, sectionOptions) : t('ph_section')
                  }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.section" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'section'" class="custom-dropdown">
                <div
                  v-for="option in sectionOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: String(formData.section).toLowerCase() === String(option.value).toLowerCase() }"
                  @click="selectOption('section', option.value)"
                >
                  {{ getLabel(option.value, sectionOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label form-label--optional">{{ t('location') }}</label>
            <div ref="locationContainerRef" class="location-input" @click="focusLocationInput">
              <span
                v-for="(tag, index) in formData.locations"
                :key="`${tag}-${index}`"
                class="location-tag"
                :class="{ 'location-tag--focused': focusedTagIndex === index }"
                tabindex="-1"
                @click.stop="onTagClick(index)"
                @keydown="handleTagKeydown($event, index)"
              >
                {{ tag }}
              </span>
              <input
                ref="locationInputRef"
                v-model="locationDraft"
                class="location-input-field"
                type="text"
                :placeholder="formData.locations.length === 0 ? t('ph_location') : ''"
                @keydown="handleLocationKeydown"
                @blur="handleLocationBlur"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"
              ><span class="required">*</span>{{ t('photoRequired') }}</label
            >
            <div class="custom-select-wrapper" :class="{ error: errors.photoRequired }" @click.stop @focusout="handlePhotoRequiredFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'photoRequired' }"
                @click="toggleDropdown('photoRequired')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.photoRequired }">
                  {{
                    formData.photoRequired
                      ? getLabel(formData.photoRequired, photoOptions)
                      : t('select')
                  }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.photoRequired" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'photoRequired'" class="custom-dropdown">
                <div
                  v-for="opt in photoOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.photoRequired === opt.value }"
                  @click="selectOption('photoRequired', opt.value)"
                >
                  {{ getLabel(opt.value, photoOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="formData.type === 'routine'" class="form-group">
            <label class="form-label"
              ><span class="required">*</span>{{ t('followUpNeeded') }}</label
            >
            <div class="custom-select-wrapper" :class="{ error: errors.followUpNeeded }" @click.stop @focusout="handleFollowUpFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'followUpNeeded' }"
                @click="toggleDropdown('followUpNeeded')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.followUpNeeded }">
                  {{
                    formData.followUpNeeded
                      ? getLabel(formData.followUpNeeded, followUpOptions)
                      : t('select')
                  }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.followUpNeeded"
                    :src="alertIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'followUpNeeded'" class="custom-dropdown">
                <div
                  v-for="opt in followUpOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.followUpNeeded === opt.value }"
                  @click="selectOption('followUpNeeded', opt.value)"
                >
                  {{ getLabel(opt.value, followUpOptions) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="formData.type === 'routine' && formData.followUpNeeded === 'yes'" class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('planIncludes') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.planIncludesFollowUp }" @click.stop @focusout="handlePlanFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'planIncludesFollowUp' }"
                @click="toggleDropdown('planIncludesFollowUp')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': formData.planIncludesFollowUp.length === 0 }">
                  {{ getPlanLabel(formData.planIncludesFollowUp) }}
                </span>
                <div class="select-trail">
                  <img
                    v-if="errors.planIncludesFollowUp"
                    :src="alertIcon"
                    alt="error"
                    class="error-icon-select"
                  />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div
                v-if="dropdownOpen === 'planIncludesFollowUp'"
                class="custom-dropdown checkbox-dropdown"
              >
                <div class="dropdown-item" :class="{ selected: isAllPlansSelected }" @click.stop="toggleAllPlans">
                  <div class="checkbox-wrapper">
                    <div class="checkbox" :class="{ checked: isAllPlansSelected }">
                      <svg v-if="isAllPlansSelected" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label all-label">{{ t('all') }}</span>
                  </div>
                </div>
                <div
                  v-for="option in planOptions"
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ selected: formData.planIncludesFollowUp.includes(option.value) }"
                  @click.stop="togglePlan(option.value)"
                >
                  <div class="checkbox-wrapper">
                    <div
                      class="checkbox"
                      :class="{ checked: formData.planIncludesFollowUp.includes(option.value) }"
                    >
                      <svg
                        v-if="formData.planIncludesFollowUp.includes(option.value)"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6L5 9L10 3"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <span class="checkbox-label">{{ option.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('frequency') }}</label>
            <div class="custom-select-wrapper" :class="{ error: errors.frequency }" @click.stop @focusout="handleFrequencyFocusOut">
              <div
                class="custom-select"
                tabindex="0"
                :class="{ 'is-open': dropdownOpen === 'frequency' }"
                @click="toggleDropdown('frequency')"
              >
                <span class="custom-select-value" :class="{ 'placeholder-text': !formData.frequency }">
                  {{
                    formData.frequency
                      ? getLabel(formData.frequency, frequencyOptions)
                      : t('ph_frequency')
                  }}
                </span>
                <div class="select-trail">
                  <img v-if="errors.frequency" :src="alertIcon" alt="error" class="error-icon-select" />
                  <img :src="downarrowIcon" alt="dropdown" class="select-arrow" />
                </div>
              </div>
              <div v-if="dropdownOpen === 'frequency'" class="custom-dropdown">
                <div
                  v-for="opt in frequencyOptions"
                  :key="opt.value"
                  class="dropdown-item"
                  :class="{ selected: formData.frequency === opt.value }"
                  @click="selectOption('frequency', opt.value)"
                >
                  {{ opt.label }}
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label"><span class="required">*</span>{{ t('description') }}</label>
            <div class="textarea-wrapper" :class="{ error: errors.description || errors.descriptionLang }">
              <textarea
                :value="currentLang === 'zh' ? formData.descriptionZh : formData.description"
                @input="handleDescriptionInput"
                class="form-textarea"
                :placeholder="t('ph_desc')"
                :maxlength="5000"
                @blur="handleDescriptionBlur"
              ></textarea>
              <div class="char-count">
                {{
                  (currentLang === 'zh' ? formData.descriptionZh : formData.description).length ||
                  0
                }}/5000
              </div>
              <img
                v-if="errors.description || errors.descriptionLang"
                :src="alertIcon"
                alt="error"
                class="error-icon-textarea"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 按钮固定在底部 -->
      <div class="form-actions">
        <div class="button-group">
          <button class="btn btn-cancel" @click="handleCancel">{{ t('cancel') }}</button>
          <button class="btn btn-submit" :disabled="submitting" @click="handleSubmit">
            {{ t('submit') }}
          </button>
        </div>
      </div>
    </div>

    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import { getChecklistDetail } from '@/api/EditItem'
import { updateChecklist, type ChecklistUpdateParams } from '@/api/EditItem'
import request from '@/api/request'
import downarrowIcon from '@/assets/downarrow@2x.png'
import photoIcon from '@/assets/photo.png'
import alertIcon from '@/assets/alert.png'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import deleteIcon from '@/assets/delete_white@2x.png'
import '@/styles/custom-select-trail.css'
import { FIELD_LIMITS } from '@/constants/fieldLimits'
import { validateFieldLimits, validateStringLength } from '@/utils/fieldLimitValidation'
import { normalizeSeasonValues } from '@/utils/seasons'
import { resolveLocationsForItem } from '@/utils/checklistLocations'
import { useLocationTagInput } from '@/utils/useLocationTagInput'
import '@/styles/checklist-location-input.css'
import {
  validateChineseText,
  validateEnglishText,
} from '@/utils/textLanguageValidation'
import ToastComponent from '@/components/ToastComponent.vue'
import { TOAST_DURATION } from '@/constants/toast'

const checklistFieldLimits = FIELD_LIMITS.checklist

interface Props {
  itemId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
  (e: 'toast', data: { type: 'success' | 'warning' | 'error'; message: string }): void
}>()

const submitting = ref(false)

interface OptionItem {
  value: string
  label?: string
  labelEn?: string
  labelZh?: string
}

const messages = {
  en: {
    title: 'Edit Item',
    type: 'Type',
    itemName: 'Item name',
    itemIcon: 'Item icon',
    duration: 'Duration',
    seasons: 'Seasons',
    section: 'Section',
    location: 'Location',
    photoRequired: 'Photo Required',
    followUpNeeded: 'Follow-up Needed (if issues found):',
    planIncludes: 'Plan includes follow-up care',
    frequency: 'Frequency',
    description: 'Description:',
    upload: 'Upload',
    cancel: 'Cancel',
    submit: 'Submit',
    select: 'Select',
    all: 'All',
    delete: 'Delete',
    ph_type: 'Select type',
    ph_name: 'Enter item name (max 200 characters)',
    ph_duration: 'Select duration',
    ph_seasons: 'Select inspection seasons',
    ph_section: 'Select section',
    ph_location: 'Enter location',
    ph_plan: 'Select plan includes follow-up care',
    ph_frequency: 'Select frequency',
    ph_desc: 'Enter check description',
  },
  zh: {
    title: 'Edit Item',
    type: 'Type',
    itemName: 'Item name (中文)',
    itemIcon: 'Item icon',
    duration: 'Duration',
    seasons: 'Seasons',
    section: 'Section',
    location: 'Location',
    photoRequired: 'Photo Required',
    followUpNeeded: 'Follow-up Needed (if issues found):',
    planIncludes: 'Plan includes follow-up care',
    frequency: 'Frequency',
    description: 'Description (中文):',
    upload: 'Upload',
    cancel: 'Cancel',
    submit: 'Submit',
    select: 'Select',
    all: 'All',
    delete: 'Delete',
    ph_type: 'Select type',
    ph_name: 'Enter item name in Chinese (max 200 characters)',
    ph_duration: 'Select duration',
    ph_seasons: 'Select inspection seasons',
    ph_section: 'Select section',
    ph_location: 'Enter location',
    ph_plan: 'Select plan includes follow-up care',
    ph_frequency: 'Select frequency',
    ph_desc: 'Enter check description in Chinese',
  },
}

const currentLang = ref<'en' | 'zh'>('en')
const fileInput = ref<HTMLInputElement | null>(null)
const dropdownOpen = ref<string | null>(null)
const previewUrl = ref<string>('')

const t = (key: keyof typeof messages.en): string => {
  return messages[currentLang.value][key]
}

interface FormData {
  type: string
  itemName: string
  itemNameZh: string
  icon: File | null
  duration: string
  seasons: string[]
  section: string
  locations: string[]
  photoRequired: string
  followUpNeeded: string
  planIncludesFollowUp: string[]
  frequency: string
  description: string
  descriptionZh: string
}

interface Errors {
  type?: boolean
  itemName?: boolean
  itemNameLang?: boolean
  icon?: boolean
  duration?: boolean
  seasons?: boolean
  section?: boolean
  locations?: boolean
  photoRequired?: boolean
  followUpNeeded?: boolean
  planIncludesFollowUp?: boolean
  frequency?: boolean
  description?: boolean
  descriptionLang?: boolean
}

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const formData = reactive<FormData>({
  type: '',
  itemName: '',
  itemNameZh: '',
  icon: null,
  duration: '',
  seasons: [],
  section: '',
  locations: [],
  photoRequired: '',
  followUpNeeded: 'yes',
  planIncludesFollowUp: [],
  frequency: '',
  description: '',
  descriptionZh: '',
})

const loadedItemId = ref('')
const errors = reactive<Errors>({})

const {
  locationDraft,
  locationInputRef,
  locationContainerRef,
  focusedTagIndex,
  focusLocationInput,
  commitLocationDraft,
  handleLocationKeydown,
  handleTagKeydown,
  handleLocationBlur,
  onTagClick,
} = useLocationTagInput(() => formData.locations)

const typeOptions = [
  { value: 'routine', labelEn: 'Routine', labelZh: 'Routine' },
  { value: 'baseline', labelEn: 'Baseline', labelZh: 'Baseline' },
]

const photoOptions = [
  { value: 'yes', labelEn: 'Yes', labelZh: 'Yes' },
  { value: 'no', labelEn: 'No', labelZh: 'No' },
]

const followUpOptions = [
  { value: 'yes', labelEn: 'Yes', labelZh: 'Yes' },
  { value: 'no', labelEn: 'No', labelZh: 'No' },
]

const frequencyOptions = [
  { value: 'annually', label: 'Annually' },
  { value: 'semi-annually', label: 'Semi-annually' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'one-time', label: 'One-time' },
]

const durationOptions = [
  { value: '5', label: '5 mins' },
  { value: '10', label: '10 mins' },
  { value: '15', label: '15 mins' },
  { value: '20', label: '20 mins' },
  { value: '25', label: '25 mins' },
  { value: '30', label: '30 mins' },
  { value: '60', label: '1 Hour' },
  { value: '90', label: '1.5 Hours' },
  { value: '120', label: '2.0 Hours' },
  { value: '150', label: '2.5 Hours' },
  { value: '180', label: '3.0 Hours' },
]

const seasonOptions = [
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' },
  { value: 'fall', label: 'Fall' },
  { value: 'winter', label: 'Winter' },
]

const sectionOptions = [
  { value: 'roofing', labelEn: 'Roofing', labelZh: 'Roofing' },
  { value: 'exterior', labelEn: 'Exterior', labelZh: 'Exterior' },
  { value: 'structure', labelEn: 'Structure', labelZh: 'Structure' },
  { value: 'electrical', labelEn: 'Electrical', labelZh: 'Electrical' },
  { value: 'heating', labelEn: 'Heating', labelZh: 'Heating' },
  { value: 'cooling', labelEn: 'Cooling', labelZh: 'Cooling' },
  { value: 'insulation', labelEn: 'Insulation', labelZh: 'Insulation' },
  { value: 'plumbing', labelEn: 'Plumbing', labelZh: 'Plumbing' },
  { value: 'interior', labelEn: 'Interior', labelZh: 'Interior' },
]

const planOptions = [
  { value: 'essential', label: 'Essential Care' },
  { value: 'enhanced', label: 'Enhanced Care' },
  { value: 'premium', label: 'Premium Care' },
]

function getLabel(value: string, options: OptionItem[]): string {
  const opt = options.find((o) => o.value === value)
  if (!opt) return value
  return opt.labelEn || opt.label || value
}

function getDurationLabel(val: string): string {
  const opt = durationOptions.find((o) => o.value === val)
  return opt ? opt.label : t('ph_duration')
}

function getPlanLabel(vals: string[]): string {
  if (vals.length === 0) return t('ph_plan')
  if (vals.length === planOptions.length) return t('all')
  return vals.map((v) => planOptions.find((o) => o.value === v)?.label || '').join(', ')
}

const isAllSeasonsSelected = computed(
  () => formData.seasons.length === seasonOptions.length && seasonOptions.length > 0,
)
const selectedSeasonOptions = computed(() =>
  seasonOptions.filter((option) => formData.seasons.includes(option.value)),
)
const isAllPlansSelected = computed(
  () => formData.planIncludesFollowUp.length === planOptions.length && planOptions.length > 0,
)

function toggleSeason(value: string): void {
  const index = formData.seasons.indexOf(value)
  if (index > -1) {
    formData.seasons.splice(index, 1)
  } else {
    formData.seasons.push(value)
  }
  if (formData.seasons.length > 0) errors.seasons = false
}

function toggleAllSeasons(): void {
  if (isAllSeasonsSelected.value) {
    formData.seasons = []
  } else {
    formData.seasons = seasonOptions.map((o) => o.value)
  }
  if (formData.seasons.length > 0) errors.seasons = false
}

function togglePlan(value: string): void {
  const index = formData.planIncludesFollowUp.indexOf(value)
  if (index > -1) {
    formData.planIncludesFollowUp.splice(index, 1)
  } else {
    formData.planIncludesFollowUp.push(value)
  }
  if (formData.planIncludesFollowUp.length > 0) errors.planIncludesFollowUp = false
}

function toggleAllPlans(): void {
  if (isAllPlansSelected.value) {
    formData.planIncludesFollowUp = []
  } else {
    formData.planIncludesFollowUp = planOptions.map((o) => o.value)
  }
  if (formData.planIncludesFollowUp.length > 0) errors.planIncludesFollowUp = false
}

function validateDropdownField(fieldName: string): void {
  if (fieldName === 'type') errors.type = isEmpty(formData.type)
  else if (fieldName === 'duration') errors.duration = isEmpty(formData.duration)
  else if (fieldName === 'seasons') errors.seasons = formData.seasons.length === 0
  else if (fieldName === 'section') errors.section = isEmpty(formData.section)
  else if (fieldName === 'photoRequired') {
    errors.photoRequired = isEmpty(formData.photoRequired)
  } else if (fieldName === 'followUpNeeded') {
    if (formData.type === 'routine') errors.followUpNeeded = isEmpty(formData.followUpNeeded)
  } else if (fieldName === 'planIncludesFollowUp') {
    if (formData.type === 'routine' && formData.followUpNeeded === 'yes') {
      errors.planIncludesFollowUp = formData.planIncludesFollowUp.length === 0
    }
  } else if (fieldName === 'frequency') {
    errors.frequency = isEmpty(formData.frequency)
  }
}

function toggleDropdown(fieldName: string): void {
  const wasOpen = dropdownOpen.value === fieldName
  dropdownOpen.value = wasOpen ? null : fieldName
  if (wasOpen) validateDropdownField(fieldName)
}

function selectOption(fieldName: string, value: string): void {
  ;(formData as Record<string, string | string[] | File | null>)[fieldName] = value

  const errorMap: Partial<Record<string, keyof Errors>> = {
    type: 'type',
    duration: 'duration',
    section: 'section',
    photoRequired: 'photoRequired',
    followUpNeeded: 'followUpNeeded',
    frequency: 'frequency',
  }
  const errorKey = errorMap[fieldName]
  if (errorKey) errors[errorKey] = false

  // 当类型从 routine 改为 baseline 时，清理 routine 特有的字段
  if (fieldName === 'type' && value === 'baseline') {
    formData.followUpNeeded = ''
    formData.planIncludesFollowUp = []
    errors.followUpNeeded = false
    errors.planIncludesFollowUp = false
    errors.frequency = false
  }

  if (fieldName === 'followUpNeeded' && value !== 'yes') {
    formData.planIncludesFollowUp = []
    errors.planIncludesFollowUp = false
  }

  dropdownOpen.value = null
}

function isEmpty(value: string | undefined | null): boolean {
  return !String(value ?? '').trim()
}

function isFocusLeavingWrapper(event: FocusEvent): boolean {
  const wrapper = event.currentTarget as HTMLElement
  return !wrapper.contains(event.relatedTarget as Node)
}

function handleTypeFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.type = isEmpty(formData.type)
}

function handleItemNameBlur() {
  if (currentLang.value === 'zh') {
    errors.itemName = false
  } else {
    errors.itemName = !formData.itemName.trim()
  }
  validateCurrentItemNameLanguage()
}

function validateCurrentItemNameLanguage(): void {
  if (currentLang.value === 'zh') {
    errors.itemNameLang =
      !formData.itemNameZh.trim() ||
      (formData.itemNameZh.trim().length > 0 && !validateChineseText(formData.itemNameZh))
  } else {
    errors.itemNameLang = formData.itemName.trim().length > 0 && !validateEnglishText(formData.itemName)
  }
}

function validateCurrentDescriptionLanguage(): void {
  if (currentLang.value === 'zh') {
    errors.descriptionLang =
      !formData.descriptionZh.trim() ||
      (formData.descriptionZh.trim().length > 0 && !validateChineseText(formData.descriptionZh))
  } else {
    errors.descriptionLang =
      formData.description.trim().length > 0 && !validateEnglishText(formData.description)
  }
}

function isEnglishTextFieldsIncomplete(): boolean {
  return (
    !formData.itemName.trim() ||
    !validateEnglishText(formData.itemName) ||
    !formData.description.trim() ||
    !validateEnglishText(formData.description)
  )
}

function validateSharedFields(): boolean {
  errors.type = !formData.type
  errors.icon = !hasItemIcon()
  errors.duration = !formData.duration
  errors.seasons = formData.seasons.length === 0
  errors.section = !formData.section
  errors.photoRequired = !formData.photoRequired
  errors.followUpNeeded = formData.type === 'routine' && !formData.followUpNeeded
  errors.planIncludesFollowUp =
    formData.type === 'routine' &&
    formData.followUpNeeded === 'yes' &&
    formData.planIncludesFollowUp.length === 0
  errors.frequency = isEmpty(formData.frequency)
  return (
    !errors.type &&
    !errors.icon &&
    !errors.duration &&
    !errors.seasons &&
    !errors.section &&
    !errors.photoRequired &&
    !errors.followUpNeeded &&
    !errors.planIncludesFollowUp &&
    !errors.frequency
  )
}

function validateEnglishTextFields(): boolean {
  const itemNameMissing = !formData.itemName.trim()
  const descriptionMissing = !formData.description.trim()
  const itemNameLangError =
    formData.itemName.trim().length > 0 && !validateEnglishText(formData.itemName)
  const descriptionLangError =
    formData.description.trim().length > 0 && !validateEnglishText(formData.description)

  errors.itemName = itemNameMissing || itemNameLangError
  errors.description = descriptionMissing || descriptionLangError

  return !itemNameMissing && !descriptionMissing && !itemNameLangError && !descriptionLangError
}

function validateChineseTextFields(required = false): boolean {
  const itemNameZhMissing = !formData.itemNameZh.trim()
  const descriptionZhMissing = !formData.descriptionZh.trim()
  const itemNameZhLangError =
    formData.itemNameZh.trim().length > 0 && !validateChineseText(formData.itemNameZh)
  const descriptionZhLangError =
    formData.descriptionZh.trim().length > 0 && !validateChineseText(formData.descriptionZh)

  errors.itemNameLang = required
    ? itemNameZhMissing || itemNameZhLangError
    : itemNameZhLangError
  errors.descriptionLang = required
    ? descriptionZhMissing || descriptionZhLangError
    : descriptionZhLangError

  if (required) {
    return (
      !itemNameZhMissing &&
      !descriptionZhMissing &&
      !itemNameZhLangError &&
      !descriptionZhLangError
    )
  }
  return !itemNameZhLangError && !descriptionZhLangError
}

function getChineseValidationMessage(): string | null {
  if (!formData.itemNameZh.trim() || !validateChineseText(formData.itemNameZh)) {
    return 'Please enter the Chinese item name.'
  }
  if (!formData.descriptionZh.trim() || !validateChineseText(formData.descriptionZh)) {
    return 'Please enter the Chinese description.'
  }
  return null
}

function getEnglishValidationMessage(): string | null {
  if (!formData.itemName.trim() || !validateEnglishText(formData.itemName)) {
    return 'Please enter the English item name.'
  }
  if (!formData.description.trim() || !validateEnglishText(formData.description)) {
    return 'Please enter the English description.'
  }
  return null
}

function getLanguageValidationMessage(): string | null {
  if (currentLang.value === 'zh') {
    return getChineseValidationMessage() || getEnglishValidationMessage()
  }
  return getEnglishValidationMessage() || getChineseValidationMessage()
}

function handleDurationFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.duration = isEmpty(formData.duration)
}

function handleSeasonsFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.seasons = formData.seasons.length === 0
}

function handleSectionFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.section = isEmpty(formData.section)
}

function handlePhotoRequiredFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  errors.photoRequired = isEmpty(formData.photoRequired)
}

function handleFollowUpFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  if (formData.type !== 'routine') return
  errors.followUpNeeded = isEmpty(formData.followUpNeeded)
}

function handlePlanFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  if (formData.type !== 'routine' || formData.followUpNeeded !== 'yes') return
  errors.planIncludesFollowUp = formData.planIncludesFollowUp.length === 0
}

function handleFrequencyFocusOut(event: FocusEvent) {
  if (!isFocusLeavingWrapper(event)) return
  if (formData.type !== 'routine') return
  errors.frequency = isEmpty(formData.frequency)
}

function handleDescriptionBlur() {
  if (currentLang.value === 'zh') {
    errors.description = false
  } else {
    errors.description = !formData.description.trim()
  }
  validateCurrentDescriptionLanguage()
}

function handleItemNameInput(event: Event): void {
  const target = event.target as HTMLInputElement
  if (currentLang.value === 'zh') {
    formData.itemNameZh = target.value
    if (target.value.trim() && validateChineseText(formData.itemNameZh)) {
      errors.itemNameLang = false
    }
  } else {
    formData.itemName = target.value
    if (target.value.trim()) {
      errors.itemName = false
    }
  }
  validateCurrentItemNameLanguage()
}

function handleDescriptionInput(event: Event): void {
  const target = event.target as HTMLTextAreaElement
  if (currentLang.value === 'zh') {
    formData.descriptionZh = target.value
    if (target.value.trim() && validateChineseText(formData.descriptionZh)) {
      errors.descriptionLang = false
    }
  } else {
    formData.description = target.value
    if (target.value.trim()) {
      errors.description = false
    }
  }
  validateCurrentDescriptionLanguage()
}

function setLanguage(lang: 'en' | 'zh'): void {
  currentLang.value = lang
  dropdownOpen.value = null
}

// ✅ 修复后的关闭逻辑：点击modal空白处触发
function closeDropdowns(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (target.closest('.custom-select-wrapper')) return
  if (dropdownOpen.value) {
    validateDropdownField(dropdownOpen.value)
    dropdownOpen.value = null
  }
}

onMounted(() => {
  if (props.itemId) {
    void fetchDetail()
  }
})

onBeforeUnmount(() => {
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
})

function showToastLocal(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
    toastTimer = null
  }, TOAST_DURATION.brief)
}

function getFullUrl(url: string): string {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://hm.back.escroud.com'
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`
}

function getRelativeUrl(url: string): string {
  if (!url) return ''
  if (!url.startsWith('http')) return url
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://hm.back.escroud.com'
  if (url.startsWith(baseUrl)) {
    return url.slice(baseUrl.length)
  }
  return url
}

async function fetchDetail(): Promise<void> {
  if (!props.itemId) {
    emit('toast', { type: 'error', message: 'Item ID not found' })
    return
  }

  try {
    console.log('=== 开始获取详情 ===')
    console.log('itemId:', props.itemId)
    const response = await getChecklistDetail(props.itemId)
    console.log('响应数据:', response)

    if (response.code === 200 && response.data) {
      const item = response.data
      console.log('项目数据:', item)

      formData.type = item.type || ''
      formData.itemName = item.item_name || ''
      formData.itemNameZh = item.item_name_zh || ''
      formData.duration = String(item.duration_minutes || '')
      formData.seasons = normalizeSeasonValues(item.seasons)
      formData.section = item.section || ''
      loadedItemId.value = item.item_id?.trim() || String(item.id)
      formData.locations = resolveLocationsForItem(item)
      formData.photoRequired = item.photo_required === 1 ? 'yes' : 'no'
      formData.followUpNeeded = item.follow_up_needed === 1 ? 'yes' : 'no'
      formData.planIncludesFollowUp = (item.follow_up_care_plans || []).map((plan: string) => {
        if (plan === 'essential_care') return 'essential'
        if (plan === 'enhanced_care') return 'enhanced'
        return 'premium'
      })
      formData.frequency = item.frequency || ''
      formData.description = item.description || ''
      formData.descriptionZh = item.description_zh || ''
      if (item.icon_url) {
        previewUrl.value = getFullUrl(item.icon_url)
      }
      console.log('formData 初始化完成:', formData)
    } else {
      console.log('获取数据失败:', response.msg)
      emit('toast', {
        type: 'error',
        message: response.msg || 'Failed to fetch data',
      })
    }
  } catch (error) {
    console.error('Fetch detail error:', error)
    emit('toast', { type: 'error', message: 'Network error' })
  }
}

function triggerFileUpload(): void {
  // 先尝试使用ref，失败则使用id选择器
  if (fileInput.value) {
    fileInput.value.click()
  } else {
    const input = document.getElementById('edit-item-file-input') as HTMLInputElement
    if (input) {
      input.click()
    }
  }
}

function hasItemIcon(): boolean {
  if (formData.icon) return true
  return Boolean(previewUrl.value?.trim())
}

function handleFileUpload(event: Event): void {
  const input = event.target as HTMLInputElement
  if (input.files && input.files[0]) {
    const file = input.files[0] as File
    formData.icon = file
    previewUrl.value = URL.createObjectURL(file)
    errors.icon = false
  }
}

function handleDeleteImage(): void {
  formData.icon = null
  if (previewUrl.value && previewUrl.value.includes('blob:')) {
    URL.revokeObjectURL(previewUrl.value)
  }
  previewUrl.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function validateForm(): boolean {
  const sharedValid = validateSharedFields()

  if (currentLang.value === 'zh') {
    const chineseValid = validateChineseTextFields(true)
    if (!chineseValid) {
      showToastLocal(
        'warning',
        getChineseValidationMessage() || 'Please enter the Chinese item name.',
      )
      return false
    }

    validateEnglishTextFields()
    if (isEnglishTextFieldsIncomplete()) {
      setLanguage('en')
      showToastLocal(
        'warning',
        getEnglishValidationMessage() || 'Please enter the English item name.',
      )
      return false
    }
  } else {
    const englishValid = validateEnglishTextFields()
    const chineseValid = validateChineseTextFields(false)
    if (!englishValid || !chineseValid) {
      showToastLocal(
        'warning',
        getLanguageValidationMessage() || 'Please complete all required fields.',
      )
      return false
    }
  }

  if (!sharedValid) {
    showToastLocal('warning', 'Please complete all required fields.')
    return false
  }

  const limitErr = validateFieldLimits([
    { value: formData.itemName, max: checklistFieldLimits.itemName, label: 'Item name' },
    { value: formData.itemNameZh, max: checklistFieldLimits.itemName, label: 'Item name' },
    { value: formData.description, max: checklistFieldLimits.description, label: 'Description' },
    { value: formData.descriptionZh, max: checklistFieldLimits.description, label: 'Description' },
  ])
  if (limitErr) {
    showToastLocal('warning', limitErr)
    return false
  }

  return true
}

async function uploadIcon(): Promise<string> {
  if (!formData.icon) return ''
  const uploadFormData = new FormData()
  uploadFormData.append('category', 'checklist_icon')
  uploadFormData.append('file', formData.icon)

  // 使用相对路径，由request自动拼接基础URL
  const res = await request.post('/api/admin/upload', uploadFormData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
  })
  const data = res as { data?: { url?: string } }
  const url = data.data?.url || ''
  return url ? getFullUrl(url) : ''
}

async function handleSubmit(): Promise<void> {
  // 未按 Enter 的草稿一并打成标签再提交
  commitLocationDraft()

  if (!validateForm()) {
    return
  }

  submitting.value = true

  let iconUrl = ''
  if (formData.icon) {
    try {
      iconUrl = await uploadIcon()
    } catch (err) {
      console.error('图片上传失败:', err)
      submitting.value = false
      showToastLocal('error', 'Icon upload failed')
      return
    }
  }

  let resolvedIconUrl = ''
  if (iconUrl) {
    resolvedIconUrl = iconUrl
  } else if (previewUrl.value && !previewUrl.value.includes('blob:')) {
    resolvedIconUrl = getRelativeUrl(previewUrl.value)
  }

  if (!resolvedIconUrl) {
    errors.icon = true
    submitting.value = false
    showToastLocal('warning', 'Please complete all required fields.')
    return
  }

  // 验证 icon_url 长度（如果有值）
  if (resolvedIconUrl) {
    const iconErr = validateStringLength(
      resolvedIconUrl,
      checklistFieldLimits.iconUrl,
      'Icon URL',
    )
    if (iconErr) {
      showToastLocal('warning', iconErr)
      submitting.value = false
      return
    }
  }

  try {
    const submitData: ChecklistUpdateParams = {
      id: Number(props.itemId),
    }
    if (formData.type) submitData.type = formData.type
    submitData.item_name = formData.itemName.trim()
    submitData.item_name_zh = formData.itemNameZh.trim()
    if (formData.duration) submitData.duration_minutes = parseInt(formData.duration)
    if (formData.seasons.length > 0) submitData.seasons = formData.seasons
    if (formData.section) submitData.section = formData.section
    submitData.locations = [...formData.locations]
    // frequency 所有类型都发送（后端要求必填）
    if (formData.frequency) {
      submitData.frequency = formData.frequency
    }

    submitData.description = formData.description.trim()
    submitData.description_zh = formData.descriptionZh.trim()

    // 始终传递 icon_url 字段，确保后端能正确处理头像更新
    submitData.icon_url = resolvedIconUrl

    // 所有类型都提交 photo_required（后端要求必填）
    submitData.photo_required = formData.photoRequired === 'yes' ? 1 : 0

    if (formData.type === 'routine') {
      submitData.follow_up_needed = formData.followUpNeeded === 'yes' ? 1 : 0
      if (formData.followUpNeeded === 'yes' && formData.planIncludesFollowUp.length > 0) {
        submitData.plan_includes_follow_up_care = formData.planIncludesFollowUp.map(
          (plan: string) => {
            if (plan === 'essential') return 'essential_care'
            if (plan === 'enhanced') return 'enhanced_care'
            return 'premium_care'
          },
        )
      }
      submitData.plan_values = {
        essential_care: 49,
        enhanced_care: 99,
        premium_care: 199,
      }
    }

    const response = await updateChecklist(submitData)
    console.log('更新响应:', response)

    if (response.code === 200) {
      emit('toast', {
        type: 'success',
        message: 'Successfully edited',
      })
      emit('saved')
      setTimeout(() => emit('close'), 2100)
    } else {
      emit('toast', {
        type: 'error',
        message: response.msg || 'Update failed',
      })
    }
  } catch (err) {
    console.error('请求错误:', err)
    emit('toast', {
      type: 'error',
      message: 'Request failed, please try again later',
    })
  } finally {
    submitting.value = false
  }
}

function handleCancel(): void {
  emit('close')
}

function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.edit-technician-page {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 530px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  flex-shrink: 0;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
}
.page-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
  padding: 0;
  white-space: nowrap;
}
.language-select-wrapper {
  position: relative;
  width: 110px;
  height: 24px;
  flex-shrink: 0;
}

.language-select-wrapper .custom-select {
  height: 24px;
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 0 16px 0 12px;
  box-sizing: border-box;
}

.btn-close {
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}


.close-icon {
  width: 16px;
  height: 16px;
}

/* ✅ modal-body启用flex纵向布局，实现按钮自动贴底 */
.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 16px 24px 0;
}

/* 表单内容区自动占满剩余空间 */
.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: block;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.required {
  color: #e54848;
  margin-right: 4px;
}

/* 非必填：占位与红 * 同宽，文字与其它标签对齐 */
.form-label--optional::before {
  content: '*';
  color: transparent;
  margin-right: 4px;
}

.form-input {
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s;
}

.form-input:hover {
  border-color: #e0e0e0;
}

.form-input:focus {
  border-color: #666666;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
  background: #fffafa;
}

.input-wrapper.error .form-input {
  border: none;
  height: 32px;
}

.error-icon {
  position: absolute;
  right: 8px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
}

.upload-container {
  position: relative;
}

.upload-container.error .upload-area {
  border-color: #ff4d4f;
  background: #fffafa;
}

.upload-area {
  width: 96px;
  height: 96px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafafa;
  transition: all 0.2s;
}

.upload-icon {
  width: 32px;
  height: 32px;
}

.upload-text {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.uploaded-image-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.delete-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.uploaded-image-wrapper:hover .delete-overlay {
  opacity: 1;
}

.delete-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.delete-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #ffffff;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.file-input {
  display: none;
}

.custom-select-wrapper {
  position: relative;
  width: 100%;
}

.custom-select-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
  background: #fffafa;
}

.custom-select-wrapper.error .custom-select {
  border: none;
  height: 32px;
}

.custom-select {
  height: 32px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background: #fff;
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

/* 下拉框展开时显示灰色边框 */
.custom-select:hover,
.custom-select:focus {
  border-color: #e0e0e0;
  outline: none;
}

.custom-select.is-open,
.custom-select.is-open:hover,
.custom-select.is-open:focus {
  border-color: #666666;
}

.select-arrow {
  transition: transform 0.2s ease;
}

.placeholder-text {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  left: 0;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10;
  overflow: hidden;
  border: none;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  padding: 8px 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.custom-dropdown .dropdown-item.selected {
  font-weight: 600 !important;
}

.checkbox-dropdown .dropdown-item.selected {
  font-weight: 600 !important
}

.checkbox-dropdown {
  max-height: 300px;
  overflow-y: auto;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
}

.checkbox.checked {
  background-color: #000000 !important;
  border-color: #000000 !important;
}

.checkbox svg {
  width: 12px;
  height: 12px;
}

.checkbox-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}

.dropdown-item.selected .checkbox-label {
  font-weight: 600;
}

.all-label {
  font-weight: normal;
}

.dropdown-item.selected .all-label {
  font-weight: 600;
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 12px;
  background: #fffafa;
}

.textarea-wrapper.error .form-textarea {
  border: none;
}

.error-icon-textarea {
  position: absolute;
  right: 12px;
  top: 12px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  pointer-events: none;
  z-index: 2;
}

.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  outline: none;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 100px;
}

.form-textarea:hover {
  border-color: #e0e0e0;
}

.form-textarea:focus {
  border-color: #666666;
}

.char-count {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: #999;
}

.toast-area {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100002;
}

/* ✅ 按钮组：和其他弹窗完全一致 */
.form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 8px 24px 24px;
  background: #fff;
  flex-shrink: 0;
}

.button-group {
  display: flex;
  gap: 8px;
}

.btn {
  width: 80px;
  height: 32px;
  padding: 0;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-cancel {
  background: #fff;
  color: #000000;
  border: 1px solid #000000;
}

.btn-cancel:hover {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.btn-submit {
  background: #000000;
  color: #fff;
}

.btn-submit:hover:not(:disabled) {
  background: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

</style>
